import { MENU_STYLE_CONFIG } from '@frontend/components/src/components/overlay/menu/menuStyleConfig';
import { sizes as themeSizes } from '@frontend/components/src/theme/foundations/sizes';
import {
  getBaseStyleObjectFromStyleConfig,
  getPartStyleFromStyleInterpolation,
  getStyleObjectFromStyleInterpolation,
  type StyleObjectMulti,
  type StyleFunctionProps,
} from '@frontend/components/src/theme/styleConfigHelpers';

import { CLOSE_BUTTON_STYLE_CONFIG } from '../closeButton/closeButtonStyleConfig';
import {
  INPUT_SIZE_TO_SIZING_DATA_MAP,
  INPUT_STYLE_CONFIG,
} from '../input/inputStyleConfig';
import { SELECT_PARTS } from './selectParts';
import type {
  SelectColorScheme,
  SelectPart,
  SelectSize,
  SelectStyleConfig,
  SelectVariant,
} from './types';

const baseStyle: SelectStyleConfig['baseStyle'] = ({ theme, colorMode }) => {
  const closeButtonStyles = getBaseStyleObjectFromStyleConfig(
    CLOSE_BUTTON_STYLE_CONFIG,
    { theme, colorMode, colorScheme: 'default' },
  );

  const inputFieldStyles = getPartStyleFromStyleInterpolation(
    INPUT_STYLE_CONFIG.baseStyle,
    'field',
    { theme, colorMode },
  );

  const menuStyles = getBaseStyleObjectFromStyleConfig(MENU_STYLE_CONFIG, {
    theme,
    colorMode,
  });

  const messageStyles = {
    h: 5,
    px: 2,
    mb: -1, // to accommodate for necessary pb: 1 on menu list
    color: 'feature.placeholder',
    fontSize: 'md',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
  };

  return {
    clearIndicator: {
      ...closeButtonStyles,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'feature.secondary',
    },
    control: {
      zIndex: 0,
      display: 'flex',
      alignItems: 'stretch',
      ...inputFieldStyles,
      p: 0,
      overflow: 'hidden',
      w: '100%',
    },
    dropdownIndicator: {
      borderRadius: 0,
      borderWidth: 0,
      cursor: 'pointer',
      color: 'feature.secondary',
    },
    group: {
      _notLast: {
        borderBottom: '1px solid',
        borderColor: 'separator.divider',
        pb: 1,
      },
    },
    groupHeading: {
      ...menuStyles?.groupTitle,
    },
    indicatorsContainer: {
      zIndex: 1,
      display: 'flex',
      flexShrink: 0,
      alignItems: 'center',
      alignSelf: 'stretch',
    },
    loadingMessage: messageStyles,
    menu: {
      zIndex: 'popover',
    },
    menuList: {
      ...menuStyles?.list,
      my: 1,
    },
    noOptionsMessage: {
      ...messageStyles,
      h: 3,
      mb: 0,
    },
    option: {
      ...menuStyles?.item,
      cursor: 'pointer',
      position: 'relative',
      h: undefined,
      py: 0.75,
      wordBreak: 'break-all',
    },
    optionSuggestionIcon: {
      boxSize: 3,
      position: 'absolute',
      right: 2, // menuStyles?.item.px
      top: 0.75, // option.py
    },
    placeholder: {
      color: 'feature.placeholder',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      left: 1,
      right: 0,
      pointerEvents: 'none',
    },
    selectContainer: {
      width: '100%',
      minWidth: 0,
    },
    singleValue: {
      color: 'feature.primary',
      mx: 0.5,
      maxWidth: 'calc(100% - 8px)',
      overflow: 'hidden',
      position: 'absolute',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      top: '50%',
      transform: 'translateY(-50%)',
    },
    multiValueLabel: {
      display: 'inline-block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      fontFamily: 'default',
      fontSize: 'sm',
      fontWeight: 'medium',
      px: 0.75,
      _focusVisible: {
        outline: 'none',
      },
    },
    multiValueRemove: {
      display: 'inline-flex',
      alignItems: 'center',
      margin: 0,
      height: 'unset',
      width: 'unset',
      pr: 0.5,
      transitionProperty: 'common',
      transitionDuration: 'normal',
      _hover: {
        color: 'interaction.destructiveHover',
      },
      _active: {
        color: 'interaction.destructiveActive',
      },
    },
    suggestionsAvailableIcon: {
      boxSize: 3,
      marginRight: 0.75,
      color: 'alert.assistance',
    },
    suggestionsRejectedIcon: {
      boxSize: 3,
      marginRight: 0.75,
      color: 'feature.placeholder',
    },
    valueContainer: {
      zIndex: 1,
      position: 'relative',
      display: 'flex',
      alignItems: 'center',
      paddingX: 1,
      flex: 1,
      flexWrap: 'wrap',
      overflow: 'hidden',
      minWidth: 0,
    },
  };
};

const getBaseSizeStyles = (
  size: SelectSize,
  { theme, colorMode }: StyleFunctionProps<SelectVariant, SelectColorScheme>,
): StyleObjectMulti<SelectPart> => {
  const inputSizingData = INPUT_SIZE_TO_SIZING_DATA_MAP[size];
  const inputHeight = inputSizingData.h;

  const inputFieldStyles = getPartStyleFromStyleInterpolation(
    INPUT_STYLE_CONFIG.sizes[size],
    'field',
    { theme, colorMode },
  );

  const closeButtonStyles = getStyleObjectFromStyleInterpolation(
    CLOSE_BUTTON_STYLE_CONFIG.sizes[size],
    { theme, colorMode, colorScheme: 'default' },
  );

  return {
    control: {
      ...inputFieldStyles,
      px: 0,
      minH: inputHeight,
      h: 'auto',
    },
    clearIndicator: {
      ...closeButtonStyles,
      mx: inputSizingData.px / 2,
    },
    dropdownIndicator: {
      w: inputHeight,
      maxH: `calc(${themeSizes[inputHeight]} - (1px * 2))`,
    },
  };
};

const SIZES: SelectStyleConfig['sizes'] = {
  lg: (styleFunctionProps) => {
    const base = getBaseSizeStyles('lg', styleFunctionProps);

    return {
      ...base,
      dropdownIndicator: {
        ...base.dropdownIndicator,
        fontSize: '2.5rem',
      },
    };
  },
  md: (styleFunctionProps) => {
    const base = getBaseSizeStyles('md', styleFunctionProps);

    return {
      ...base,
      multiValueLabel: {
        lineHeight: '1.35rem',
      },
      dropdownIndicator: {
        ...base.dropdownIndicator,
        fontSize: '2rem',
      },
    };
  },
  sm: (styleFunctionProps) => {
    const base = getBaseSizeStyles('sm', styleFunctionProps);

    return {
      ...base,
      multiValueLabel: {
        lineHeight: '1.125rem',
      },
      dropdownIndicator: {
        ...base.dropdownIndicator,
        fontSize: '1.5rem',
      },
    };
  },
};

const DEFAULT_PROPS = {
  size: 'md',
} as const;

export const SELECT_STYLE_CONFIG: SelectStyleConfig = {
  parts: SELECT_PARTS,
  baseStyle,
  sizes: SIZES,
  defaultProps: DEFAULT_PROPS,
};
